<template>
    <div class="pe-0 pb-5 service-builder">
        <LoaderComponent v-if="isLoading" />
        <div v-else>
            <div class="service-builder__content">
                <div ref="stickyHeader" class="service-builder__sticky">
                    <ServiceBuilderHeader
                        :title-html="headerText"
                        :days-label="selectedDaysLabel"
                        :form-id="formId"
                        :is-saving="formLoading"
                        @cancel="goBack" />
                </div>
                <div
                    v-if="errors && Object.values(errors).length > 0"
                    ref="errorSummary"
                    class="p-3 mb-3 rounded alert alert-danger position-static border-0"
                    role="alert"
                    aria-atomic="true"
                    tabindex="-1">
                    <ShowErrors :errors="errors" errorKey="*" errClass="" />
                </div>
                <nav class="service-builder__local-nav" aria-label="Navigation du formulaire service" role="tablist">
                    <button
                        v-for="section in builderNavigationSections"
                        :key="section.key"
                        type="button"
                        role="tab"
                        class="service-builder__nav-item"
                        :class="{ 'is-active': activeBuilderSection === section.key }"
                        :aria-selected="activeBuilderSection === section.key ? 'true' : 'false'"
                        @click="activeBuilderSection = section.key">
                        <span class="service-builder__nav-copy">
                            <span class="service-builder__nav-title">{{ section.title }}</span>
                            <small>{{ section.description }}</small>
                        </span>
                        <span class="service-builder__nav-end">
                            <feather type="chevron-right" size="16" class="service-builder__nav-cue" />
                        </span>
                    </button>
                </nav>
                <section v-if="activeBuilderSection !== 'payments'" class="service-builder__context" aria-live="polite">
                    <div class="service-builder__context-copy">
                        <span class="service-builder__context-eyebrow">{{ activeBuilderContext.eyebrow }}</span>
                        <h6>{{ activeBuilderContext.title }}</h6>
                        <p>{{ activeBuilderContext.description }}</p>
                    </div>
                    <div class="service-builder__context-metrics">
                        <span v-for="metric in activeBuilderContext.metrics" :key="metric.label" class="service-builder__context-metric">
                            <small>{{ metric.label }}</small>
                            <strong>{{ metric.value }}</strong>
                        </span>
                    </div>
                </section>
                <form :id="formId" @submit.prevent="postForm">
                    <div v-if="isGeneralBuilderSection" class="service-builder__section-panel">
                        <GeneralGroup
                            @has-errors="handleErrors"
                            @clear-errors="errors = []"
                            @update-external-menus="updateExternalMenus"
                            @toggle-special="specialEnabled = $event"
                            :isSpecial="specialEnabled"
                            :showSpecialToggle="specialEnabled"
                            :builderMode="true"
                            :seatingPlans="seatingPlans"
                            :service="service"
                            :widget="widget"
                            :restaurant="restaurant"
                            :restaurantsToSuggest="restaurantsToSuggest"
                            :menus="menus"
                            :generalOptions="generalOptions"
                            :externalMenuFromProps="externalMenu"
                            :isSaving="formLoading"
                            :visibleSections="activeGeneralBuilderSections" />
                    </div>
                    <div v-if="activeBuilderSection === 'menus'" class="service-builder__section-panel">
                        <MenuAndOptionsGroup
                            @fetch-menus="fetchMenus"
                            @fetch-options="fetchOptions"
                            @update-menus="updateMenus"
                            @update-general-options="updateGeneralOptions"
                            :menus="menus"
                            :generalOptions="generalOptions"
                            :service="service" />
                        <GeneralGroup
                            @has-errors="handleErrors"
                            @clear-errors="errors = []"
                            @update-external-menus="updateExternalMenus"
                            @toggle-special="specialEnabled = $event"
                            :isSpecial="specialEnabled"
                            :showSpecialToggle="false"
                            :builderMode="true"
                            :seatingPlans="seatingPlans"
                            :service="service"
                            :widget="widget"
                            :restaurant="restaurant"
                            :restaurantsToSuggest="restaurantsToSuggest"
                            :menus="menus"
                            :generalOptions="generalOptions"
                            :externalMenuFromProps="externalMenu"
                            :isSaving="formLoading"
                            :visibleSections="['externalMenu']" />
                    </div>
                    <div v-if="activeBuilderSection === 'payments'" class="service-builder__section-panel">
                        <PaymentGroup :service="service" :restaurant="restaurant" :widget="widget" :show-reminder-settings="false" />
                    </div>
                    <div v-if="activeBuilderSection === 'googleReserve'" class="service-builder__section-panel">
                        <googleReserve
                            v-if="hasEnabledGoogleReserve"
                            :forServices="true"
                            :settings="service.googleReserveSettings"
                            :restaurantId="restaurant.id" />
                    </div>
                    <div v-if="activeBuilderSection === 'advanced'" class="service-builder__section-panel">
                        <MiscGroup :service="service" :restaurant="restaurant" :restaurantsToSuggest="restaurantsToSuggest" />
                    </div>
                </form>
            </div>
        </div>
        <no-seating-plan-modal v-if="showNoSeatingPlanModal" closeButtonTitle="Retour" @close="showNoSeatingPlanModal = false" />
        <confirm-opening-create-modal
            v-if="showConfirmModal"
            @close="showConfirmModal = false"
            @confirmed="sendForm"
            :formLoading="formLoading"
            :date_begin="service.general.special?.openDate"
            :date_end="service.general.special?.closeDate"
            :servicesId="showConfirmModalServicesId"
            :cancelData="confirmModalData" />
        <confirm-delete-slots-modal
            v-if="showConfirmDeleteSlotsModal"
            :headerText="headerText"
            :reservations="futuresReservations"
            :loading="formLoading"
            @confirmed-without-cancel="postForm(null, true)"
            @confirmed="cancelReservations"
            @close="showConfirmDeleteSlotsModal = false" />
    </div>
</template>

<script>
import axios from "axios";
import GeneralGroup from "../../../../../components/services/addServiceForm/GeneralGroup.vue";
import MenuAndOptionsGroup from "../../../../../components/services/addServiceForm/MenuAndOptionsGroup.vue";
import PaymentGroup from "../../../../../components/services/addServiceForm/PaymentGroup.vue";
import MiscGroup from "../../../../../components/services/addServiceForm/MiscGroup.vue";
import googleReserve from "../../../../../components/services/googleReserve.vue";
import LoaderComponent from "../../../../../components/LoaderComponent.vue";
import NoSeatingPlanModal from "../../../../../components/Modals/seatingPlan/NoSeatingPlanModal.vue";
import ShowErrors from "../../../../../components/errors/ShowErrors.vue";
import ConfirmOpeningCreateModal from "../../../../../components/Modals/openings/ConfirmOpeningCreateModal.vue";
import ConfirmDeleteSlotsModal from "../../../../../components/Modals/ConfirmDeleteSlotsModal.vue";
import ExternalMenuTypeEnum from "../../../../../mixins/enums/booking/ExternalMenuTypeEnum.js";
import DefaultServiceCategoriesEnum from "../../../../../mixins/enums/booking/DefaultServiceCategoriesEnum.js";
import ServiceBuilderHeader from "./components/ServiceBuilderHeader.vue";
import {
    buildPaxesDurations,
    formatDayLabel,
    mapMenusFromApi,
    mapRestaurantsProposals,
    mapSlotsFromApi,
    normalizeDays,
} from "./helpers/serviceBuilderUtils";

const FORM_ID = "service-builder-form";
const DAY_INDEX_BY_NAME = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

export default {
    name: "ServiceBuilderV2",
    data() {
        return this.$_.cloneDeep(this.defaultData());
    },
    mixins: [ExternalMenuTypeEnum, DefaultServiceCategoriesEnum],
    computed: {
        formId() {
            return FORM_ID;
        },
        restaurant_id() {
            return this.$route.params.restaurant_id;
        },
        service_id() {
            return this.$route.params.service_id;
        },
        hasEnabledGoogleReserve() {
            return this.$store.getters["widgets/getWidget"].enable_google_reserve || false;
        },
        restaurantFromStore() {
            return this.$store.getters["restaurants/findRestaurantById"](this.restaurant_id);
        },
        hasFeatSeatingPlan() {
            return typeof this.restaurantFromStore !== "undefined" && this.restaurantFromStore.feat_seating_plan;
        },
        isEditing() {
            return !!this.service_id && !this.isDuplicating;
        },
        headerText() {
            if (this.service_id && !this.isDuplicating) {
                return `${this.$tl("labels.booking.services.edit", this.restaurant_id)} <span class="value">${
                    this.service.general.name || ""
                }</span>`;
            }
            if (this.service_id && this.isDuplicating) {
                return this.$tl("labels.booking.services.duplicate", this.restaurant_id);
            }
            return this.$tl("labels.booking.services.add", this.restaurant_id);
        },
        selectedDaysLabel() {
            const days = this.service?.general?.openDays || [];
            if (!Array.isArray(days) || days.length === 0) {
                return null;
            }

            const translate = (key) => this.$t(key);
            return days
                .map((day) => formatDayLabel(translate, day))
                .filter((day) => day)
                .join(", ");
        },
        builderNavigationSections() {
            const sections = [
                {
                    key: "general",
                    title: "Général",
                    description: "Identité et horaires",
                },
                {
                    key: "capacity",
                    title: "Capacité",
                    description: "Couverts et limites",
                },
                {
                    key: "rules",
                    title: "Règles",
                    description: "Réservation et salle",
                },
                {
                    key: "menus",
                    title: "Menus & options",
                    description: "Menus, options, externe",
                },
                {
                    key: "payments",
                    title: "Garanties",
                    description: "Demande CB, empreinte et prépaiement",
                },
            ];

            if (this.hasEnabledGoogleReserve) {
                sections.push({
                    key: "googleReserve",
                    title: "Réserver avec Google",
                    description: "Diffusion Google",
                });
            }

            sections.push({
                key: "advanced",
                title: "Avancé",
                description: "Paramètres avancés",
            });

            return sections;
        },
        generalBuilderSectionKeys() {
            return ["general", "capacity", "rules"];
        },
        isGeneralBuilderSection() {
            return this.generalBuilderSectionKeys.includes(this.activeBuilderSection);
        },
        activeGeneralBuilderSections() {
            if (!this.isGeneralBuilderSection) {
                return [];
            }
            if (this.activeBuilderSection === "general") {
                return ["identity", "planning"];
            }

            return [this.activeBuilderSection];
        },
        slotsCount() {
            return Object.keys(this.service.general.slots || {}).length;
        },
        selectedMenusCount() {
            return Object.keys(this.service.menus.value || {}).length;
        },
        selectedOptionsCount() {
            return Array.isArray(this.service.generalOptions.value) ? this.service.generalOptions.value.length : 0;
        },
        selectedRestaurantsCount() {
            return Array.isArray(this.service.full.restaurantsProposals) ? this.service.full.restaurantsProposals.length : 0;
        },
        serviceCategoryLabel() {
            const category = this.service.general.category.value;
            if (!category) {
                return "À renseigner";
            }
            if (category === this.SERVICE_CATEGORY_AUTRE.value) {
                return this.service.general.category.nameFr || "Personnalisée";
            }

            return this.getDefaultServiceCategoryLabel(category);
        },
        externalMenuLabel() {
            if (this.externalMenu.type === this.EXTERNAL_MENU_FILE.value) {
                return "PDF";
            }
            if (this.externalMenu.type === this.EXTERNAL_MENU_LINK.value) {
                return "Lien";
            }

            return "Désactivé";
        },
        paymentSummaryLabel() {
            const modes = [];
            if (this.service.payment.bankImprint.enabled) {
                modes.push("demande de carte");
            }
            if (this.service.payment.bankImprint.noShowGuaranteeEnabled) {
                modes.push("garantie no-show");
            }
            if (this.service.payment.bankImprint.securityEnabled) {
                modes.push("empreinte bancaire");
            }
            if (this.service.payment.prepayment.enabled) {
                modes.push("prépaiement");
            }

            return modes.length ? modes.join(" + ") : "Désactivé";
        },
        noShowAmountSummaryLabel() {
            const bankImprint = this.service.payment.bankImprint;
            if (!bankImprint.enabled || !bankImprint.noShowGuaranteeEnabled) {
                return "Désactivé";
            }

            const unit = bankImprint.amountMode === "per_booking" ? "par réservation" : "par couvert";
            return `${bankImprint.amount || 0} € ${unit}`;
        },
        activeBuilderContext() {
            const daysLabel = this.selectedDaysLabel || "Aucun jour";
            const minPeople = this.service.general.use_widget_min_pers ? this.widget.min_pax : this.service.general.min_pers;
            const maxPeople = this.service.general.use_widget_max_pers ? this.maxPax : this.service.general.max_pers;
            const minPeopleLabel = minPeople ? `${minPeople} ${Number(minPeople) === 1 ? "personne" : "personnes"}` : "À renseigner";
            const maxPeopleLabel = maxPeople ? `${maxPeople} ${Number(maxPeople) === 1 ? "personne" : "personnes"}` : "À renseigner";
            const customizedSlotsCount = Object.values(this.service.general.slots || {}).filter(
                (slot) => slot.is_max_pax_enabled || slot.is_max_reservations_enabled
            ).length;
            const lockLabel = this.service.general.lock.enabled ? `${this.service.general.lock.until} min avant` : "Jusqu'au live";
            const cancellationLabel = `${this.service.general.cancellation.delay || 0} h avant`;
            const seatingPlanLabel = this.service.seatingPlan.algorithm_enabled ? "Placement auto" : "Manuel";
            const googleLabel = this.service.googleReserveSettings.enabled ? "Activé" : "Désactivé";

            const contexts = {
                general: {
                    eyebrow: "Base du service",
                    title: "Informations générales et horaires",
                    description: "Définissez l'identité du service, ses jours d'ouverture, ses horaires et les créneaux proposés à la réservation.",
                    metrics: [
                        { label: "Nom", value: this.service.general.name || "À renseigner" },
                        { label: "Jours", value: daysLabel },
                        {
                            label: "Horaires",
                            value: `${this.service.general.openHours.start || "--:--"} - ${this.service.general.openHours.end || "--:--"}`,
                        },
                        { label: "Créneaux", value: `${this.slotsCount}` },
                    ],
                },
                capacity: {
                    eyebrow: "Capacité",
                    title: "Couverts et limites par créneau",
                    description: "Définissez les valeurs par défaut appliquées aux créneaux, puis personnalisez uniquement les exceptions.",
                    metrics: [
                        { label: "Minimum par réservation", value: minPeopleLabel },
                        { label: "Maximum par réservation", value: maxPeopleLabel },
                        { label: "Créneaux personnalisés", value: `${customizedSlotsCount}` },
                    ],
                },
                rules: {
                    eyebrow: "Règles de réservation",
                    title: "Visibilité, annulation et confirmation",
                    description:
                        "Ces réglages pilotent l'accès au service, le délai de réservation, l'annulation client et la confirmation automatique.",
                    metrics: [
                        { label: "Widget", value: this.service.general.show_on_widget ? "Visible" : "Masqué" },
                        { label: "Réservation", value: lockLabel },
                        { label: "Annulation", value: cancellationLabel },
                        { label: "Salle", value: seatingPlanLabel },
                    ],
                },
                menus: {
                    eyebrow: "Menus et options",
                    title: "Menus, options et menu externe",
                    description: "Associez l'offre vendue sur ce service et, si besoin, un menu externe visible côté client.",
                    metrics: [
                        { label: "Menus", value: `${this.selectedMenusCount}` },
                        { label: "Options", value: `${this.selectedOptionsCount}` },
                        { label: "Menu externe", value: this.externalMenuLabel },
                    ],
                },
                payments: {
                    eyebrow: "Sécurisation",
                    title: "Demande de carte, empreinte bancaire et prépaiement",
                    description: "Définissez les garanties demandées au client pour ce service.",
                    metrics: [
                        { label: "Modes", value: this.paymentSummaryLabel },
                        { label: "Montant no-show", value: this.noShowAmountSummaryLabel },
                        {
                            label: "Si l’empreinte échoue",
                            value: this.service.payment.bankImprint.securityEnabled
                                ? this.service.payment.autoCancel
                                    ? "Annulation auto"
                                    : "À vérifier"
                                : "Sans empreinte",
                        },
                    ],
                },
                googleReserve: {
                    eyebrow: "Canal externe",
                    title: "Réserver avec Google",
                    description: "Activez la diffusion du service sur Google lorsque les prérequis du widget le permettent.",
                    metrics: [
                        { label: "Statut", value: googleLabel },
                        { label: "Service", value: this.service.general.name || "À renseigner" },
                    ],
                },
                advanced: {
                    eyebrow: "Paramètres complémentaires",
                    title: "Suggestions et réglages avancés",
                    description: "Regroupez ici les options moins fréquentes pour garder le parcours principal plus lisible.",
                    metrics: [{ label: "Restaurants suggérés", value: `${this.selectedRestaurantsCount}` }],
                },
            };

            return contexts[this.activeBuilderSection] || contexts.general;
        },
        maxPax() {
            if (this.widget && this.widget.max_pax) {
                if (this.widget.enable_children && this.widget.max_children && this.widget.enable_children === 1) {
                    return this.widget.max_pax + this.widget.max_children;
                }

                return this.widget.max_pax;
            }
            return 0;
        },
        cancelData() {
            return {
                slots: Object.values(this.service.general.slots)
                    .filter((s) => s.hour_start)
                    .map((s) => s.hour_start),
                days: this.service.general.openDays.map((day) => DAY_INDEX_BY_NAME[day]).filter((day) => typeof day !== "undefined"),
            };
        },
    },
    watch: {
        errors(errors) {
            if (errors && Object.values(errors).length > 0) {
                this.$nextTick(this.focusErrorSummary);
            }
        },
        restaurant_id() {
            this.isLoading = true;
            this.fetchData().finally(() => {
                this.restaurant.id = this.restaurant_id;
                this.isLoading = false;
            });
        },
        specialEnabled(newVal) {
            this.applySpecialMode(newVal);
        },
        "service.seatingPlan.algorithm_enabled": {
            handler(newVal) {
                if (newVal === true && !this.restaurant.hasASeatingPlan) {
                    this.$nextTick(() => {
                        this.$set(this.service.seatingPlan, "algorithm_enabled", false);
                        this.showNoSeatingPlanModal = true;
                    });
                }
            },
        },
    },
    created() {
        this.fetchData().finally(() => {
            this.restaurant.id = this.restaurant_id;
            this.isLoading = false;
        });
    },
    methods: {
        focusErrorSummary() {
            const summary = this.$refs.errorSummary;
            if (!summary) {
                return;
            }

            summary.style.scrollMarginTop = `${this.$refs.stickyHeader.getBoundingClientRect().bottom + 12}px`;
            summary.focus({ preventScroll: true });
            summary.scrollIntoView({ block: "start", behavior: "instant" });
        },
        normalizeLockReservationDisplay(value) {
            const minutes = Number(value || 0);
            if (minutes >= 60 && minutes % 60 === 0) {
                return {
                    until: minutes / 60,
                    unit: "hours",
                };
            }

            return {
                until: minutes || 5,
                unit: "minutes",
            };
        },
        defaultData() {
            return {
                showConfirmModal: false,
                showConfirmModalServicesId: "",
                showConfirmDeleteSlotsModal: false,
                futuresReservations: [],
                confirmModalData: {
                    message_object: this.$tl("labels.booking.reservations.defaultMailObj"),
                    message_body: "",
                    cancel_enabled: false,
                    reservations_ids: "",
                },
                seatingPlans: [],
                restaurant: {
                    id: null,
                    has_psp: false,
                    hasASeatingPlan: false,
                },
                widget: {
                    min_pax: null,
                    max_pax: null,
                },
                restaurantsToSuggest: [],
                menus: [],
                generalOptions: [],
                showNoSeatingPlanModal: false,
                service: this.defaultService(),
                errors: [],
                isLoading: true,
                formLoading: false,
                externalMenu: {
                    type: null,
                    content: {
                        link: "",
                        file: "",
                    },
                    hasFile: false,
                    file: null,
                },
                isDuplicating: false,
                specialEnabled: false,
                activeBuilderSection: "general",
            };
        },
        defaultService() {
            return {
                general: {
                    category: {
                        value: null,
                        nameFr: null,
                        nameEn: null,
                    },
                    name: null,
                    openDays: [],
                    openHours: {
                        start: "00:00",
                        end: "00:00",
                    },
                    slots: {},
                    pax: {
                        max: null,
                        perSlot: false,
                    },
                    show_on_widget: true,
                    cancellation: {
                        delay: 24,
                    },
                    lock: {
                        enabled: false,
                        until: 60,
                        unit: "minutes",
                    },
                    autoconfirm: {
                        enabled: false,
                        upToPercentFull: 80,
                        upToPax: 10,
                        upToMinutesBeforeStart: 60,
                        upToNoshow: 0,
                    },
                    is_slot_max_reservations_enabled: 1,
                    is_slot_max_pax_enabled: 1,
                    defaultSlotMaxReservations: null,
                    use_widget_min_pers: true,
                    min_pers: null,
                    use_widget_max_pers: true,
                    max_pers: null,
                },
                paxes: {
                    rotation: false,
                    value: [],
                },
                menus: {
                    value: {},
                    mustBeUnique: false,
                    isUniqueAbovePax: 8,
                    mandatory: false,
                    mandatoryAbovePax: null,
                },
                generalOptions: {
                    value: [],
                },
                payment: {
                    bankImprint: {
                        enabled: false,
                        securityEnabled: false,
                        noShowGuaranteeEnabled: false,
                        cardCaptureEnabledAbovePax: 1,
                        enabledAbovePax: 1,
                        amount: 20,
                        amountMode: "per_cover",
                        captureLeadDays: 3,
                    },
                    prepayment: {
                        enabled: false,
                        allowPayOnSite: true,
                        enabledAbovePax: 5,
                        calculationMode: "cart_percentage",
                        percentage: 50,
                        fixedAmount: 20,
                    },
                    autoCancel: false,
                },
                full: {
                    restaurantsProposals: [],
                },
                seatingPlan: {
                    algorithm_enabled: false,
                    allow_clients_choose_room: false,
                    allow_clients_choose_room_mandatory: 0,
                    value: null,
                },
                googleReserveSettings: {
                    enabled: false,
                },
                sendReminderPayment: {
                    isAuto: false,
                    hours: 12,
                },
            };
        },
        handleErrors(value) {
            if (this.$_.isArray(value)) {
                this.errors = value;
            } else if (this.$_.isString(value)) {
                this.errors = [value];
            }
        },
        applySpecialMode(enabled) {
            if (enabled) {
                if (!this.service.general.special) {
                    this.$set(this.service.general, "special", {
                        openDate: this.getDateTime(),
                        closeDate: this.getDateTime(),
                    });
                }
                if (!this.service.general.openDays || this.service.general.openDays.length === 0) {
                    this.service.general.openDays = this.englishDays();
                }
            } else if (this.service.general.special) {
                this.$delete(this.service.general, "special");
            }
        },
        syncRouteFlags() {
            const duplicate = this.$route.query.duplicate;
            const special = this.$route.query.special;
            this.isDuplicating = duplicate === "1" || duplicate === "true" || duplicate === true;
            this.specialEnabled = special === "1" || special === "true" || special === true;
            this.applySpecialMode(this.specialEnabled);
        },
        resetData() {
            Object.assign(this.$data, this.$_.cloneDeep(this.defaultData()));
        },
        fetchData() {
            this.resetData();
            this.syncRouteFlags();

            const promises = [this.fetchWidget(), this.fetchRestaurantsToSuggest(), this.fetchMenus(), this.fetchOptions()];

            if (this.hasFeatSeatingPlan) {
                promises.push(this.fetchSeatingPlan());
            }

            return Promise.all(promises)
                .then(() => {
                    if (this.service_id) {
                        return this.fetchService();
                    }
                })
                .catch((error) => {
                    this.errors = [this.getErrorMsgFromErrorResponse(error)];
                });
        },
        fetchSeatingPlan() {
            return this.httpGet(`/api/restaurants/${this.restaurant_id}/seatingPlans`).then((response) => {
                if (response !== false && response.data.data.length > 0) {
                    this.restaurant.hasASeatingPlan = true;
                    this.seatingPlans = response.data.data;
                }
            });
        },
        fetchWidget() {
            return this.httpGet(`/api/restaurants/${this.restaurant_id}/widget`).then((response) => {
                if (response !== false) {
                    this.widget.min_pax = isNaN(Number.parseInt(response.data.min_pers)) ? null : Number.parseInt(response.data.min_pers);
                    this.widget.max_pax = isNaN(Number.parseInt(response.data.max_pers)) ? null : Number.parseInt(response.data.max_pers);
                    this.widget.enable_children = response.data.enable_children;
                    this.widget.max_children = isNaN(Number.parseInt(response.data.max_children))
                        ? null
                        : Number.parseInt(response.data.max_children);
                    if (this.widget.min_pax === null || this.widget.max_pax === null) {
                        this.errors = [this.$tl("errors.common.unknown")];
                    }
                }
            });
        },
        fetchRestaurantsToSuggest() {
            return this.httpGet(`/api/booking/restaurants?all=1&exclude_canceled=1`).then((response) => {
                if (response !== false) {
                    this.restaurantsToSuggest = this.filterRestaurantsToSuggest(response.data.data);
                }
            });
        },
        fetchMenus() {
            return this.httpGet(`/api/restaurants/${this.restaurant_id}/menus?all=1`).then((response) => {
                if (response !== false) {
                    this.menus = response.data.data;
                }
            });
        },
        fetchOptions() {
            return this.httpGet(`/api/restaurants/${this.restaurant_id}/general/options`).then((response) => {
                if (response !== false) {
                    this.generalOptions = response.data.data;
                }
            });
        },
        fetchService() {
            const api =
                `/api/restaurants/${this.restaurant_id}/services/${this.service_id}` +
                "?include=slots,general_options,paxDurations,menus,restaurants_proposal,service_seating_plan";
            return axios
                .get(api)
                .then((response) => {
                    this.applyServiceFromApi(response.data);
                })
                .catch((error) => {
                    this.errors = [this.getErrorMsgFromErrorResponse(error)];
                });
        },
        applyServiceFromApi(serviceData) {
            this.service.general.name = this.isDuplicating ? `${serviceData.name} (${this.$tl("labels.replica")})` : serviceData.name;

            if (this.inEnum(serviceData.category, this.ALL_DEFAULT_SERVICE_CATEGORIES)) {
                this.service.general.category.value = serviceData.category;
                this.service.general.category.nameFr = null;
                this.service.general.category.nameEn = null;
            } else {
                this.service.general.category.value = this.SERVICE_CATEGORY_AUTRE.value;
                this.service.general.category.nameFr = serviceData.category;
                this.service.general.category.nameEn = serviceData.category_en;
            }

            this.service.general.openDays = normalizeDays(serviceData.days, this.englishDays());
            this.service.general.openHours.start = serviceData.hour_begin;
            this.service.general.openHours.end = serviceData.hour_end;
            this.service.general.pax.max = serviceData.default_slot_max_pax || serviceData.max_pax;
            this.service.general.defaultSlotMaxReservations = serviceData.default_slot_max_reservations;
            this.service.general.lock.enabled = serviceData.lock_reservation_until_enabled === 1;
            const lockReservation = this.normalizeLockReservationDisplay(serviceData.lock_reservation_until);
            this.service.general.lock.until = lockReservation.until;
            this.service.general.lock.unit = lockReservation.unit;
            this.service.general.autoconfirm.enabled = serviceData.autoconfirm;
            this.service.general.autoconfirm.upToPercentFull = serviceData.autoconfirm_percent;
            this.service.general.autoconfirm.upToPax = serviceData.autoconfirm_max_pers;
            this.service.general.autoconfirm.upToMinutesBeforeStart = serviceData.autoconfirm_until;
            this.service.general.autoconfirm.upToNoshow = serviceData.lock_nb_noshow;
            this.service.general.cancellation.delay = serviceData.cancellation_until / 60;
            this.service.general.is_slot_max_reservations_enabled = serviceData.is_slot_max_reservations_enabled;
            this.service.general.is_slot_max_pax_enabled = serviceData.is_slot_max_pax_enabled;
            this.service.general.pax.perSlot = serviceData.enable_pax_per_slot;
            this.service.general.use_widget_min_pers = serviceData.min_pers === null;
            this.service.general.use_widget_max_pers = serviceData.max_pers === null;
            this.service.general.min_pers = serviceData.min_pers;
            this.service.general.max_pers = serviceData.max_pers;
            this.service.general.show_on_widget = serviceData.show_on_widget;

            this.service.general.slots = mapSlotsFromApi(serviceData.slots?.data || []);

            this.service.paxes.rotation = serviceData.is_table_rotation_enabled;
            this.service.paxes.value = buildPaxesDurations(serviceData.paxDurations?.data || [], this.maxPax);

            this.service.menus.mandatory = serviceData.menu_mandatory;
            this.service.menus.mandatoryAbovePax = serviceData.menu_mandatory_from;
            this.service.menus.mustBeUnique = serviceData.menu_unique;
            this.service.menus.isUniqueAbovePax = serviceData.menu_unique_from;
            this.service.menus.value = mapMenusFromApi(serviceData.menus?.data || []);

            this.service.generalOptions.value = (serviceData.general_options?.data || []).map((item) => item.id);

            const securePayment = serviceData.secure_payment || {};
            this.service.payment.bankImprint.enabled =
                typeof securePayment.card_capture_enabled === "boolean"
                    ? securePayment.card_capture_enabled
                    : Boolean(serviceData.enable_footprint || serviceData.prepayment_enabled);
            this.service.payment.bankImprint.securityEnabled =
                typeof securePayment.bank_imprint_enabled === "boolean" ? securePayment.bank_imprint_enabled : Boolean(serviceData.enable_footprint);
            this.service.payment.bankImprint.noShowGuaranteeEnabled =
                this.service.payment.bankImprint.securityEnabled ||
                (typeof securePayment.no_show_amount_per_cover_cents === "number"
                    ? securePayment.no_show_amount_per_cover_cents > 0
                    : Number(serviceData.amount_noshow || 0) > 0);
            this.service.payment.bankImprint.cardCaptureEnabledAbovePax =
                securePayment.card_capture_enabled_above_pax ?? securePayment.bank_imprint_enabled_above_pax ?? serviceData.footprint_min_pers ?? 1;
            this.service.payment.bankImprint.enabledAbovePax =
                securePayment.bank_imprint_enabled_above_pax ??
                serviceData.footprint_min_pers ??
                this.service.payment.bankImprint.cardCaptureEnabledAbovePax;
            this.service.payment.bankImprint.amount =
                typeof securePayment.no_show_amount_per_cover_cents === "number"
                    ? securePayment.no_show_amount_per_cover_cents / 100
                    : serviceData.amount_noshow;
            this.service.payment.bankImprint.amountMode = securePayment.no_show_amount_mode || "per_cover";
            this.service.payment.bankImprint.captureLeadDays = Math.min(
                securePayment.bank_imprint_capture_lead_days ?? this.service.payment.bankImprint.captureLeadDays,
                3
            );
            this.service.payment.prepayment.enabled =
                typeof securePayment.prepayment_enabled === "boolean" ? securePayment.prepayment_enabled : Boolean(serviceData.prepayment_enabled);
            this.service.payment.prepayment.percentage = securePayment.prepayment_percent ?? serviceData.prepayment_percent;
            this.service.payment.prepayment.calculationMode = securePayment.prepayment_calculation_mode || "cart_percentage";
            this.service.payment.prepayment.fixedAmount =
                typeof securePayment.prepayment_fixed_amount_cents === "number"
                    ? securePayment.prepayment_fixed_amount_cents / 100
                    : this.service.payment.prepayment.fixedAmount;
            this.service.payment.prepayment.allowPayOnSite =
                typeof securePayment.prepayment_allow_pay_on_site === "boolean"
                    ? securePayment.prepayment_allow_pay_on_site
                    : securePayment.prepayment_requirement
                      ? securePayment.prepayment_requirement === "optional"
                      : serviceData.prepayment_mandatory !== null
                        ? !Boolean(serviceData.prepayment_mandatory)
                        : true;
            this.service.payment.prepayment.enabledAbovePax = securePayment.prepayment_enabled_above_pax ?? serviceData.prepayment_min_pers;
            this.service.payment.autoCancel =
                securePayment.bank_imprint_failure_action === "cancel_reservation" ||
                (securePayment.bank_imprint_failure_action === undefined && serviceData.option_bank_auto_cancel !== null
                    ? Boolean(serviceData.option_bank_auto_cancel)
                    : false);

            this.service.full.restaurantsProposals = mapRestaurantsProposals(serviceData.restaurants_proposal?.data || [], this.restaurantsToSuggest);

            this.service.seatingPlan.algorithm_enabled = serviceData.is_seating_plan_algorithm_enabled;
            this.service.seatingPlan.value = serviceData.service_seating_plan || null;
            this.service.seatingPlan.allow_clients_choose_room = serviceData.allow_clients_choose_room;
            this.service.seatingPlan.allow_clients_choose_room_mandatory = serviceData.allow_clients_choose_room_mandatory;

            this.service.googleReserveSettings.enabled = serviceData.enable_google_reserve;
            this.service.sendReminderPayment.hours = serviceData.reminder_payment_hours;
            this.service.sendReminderPayment.isAuto = serviceData.reminder_payment_is_auto;

            if (serviceData.external_menu) {
                this.externalMenu.content.file = serviceData.external_menu.file ?? "";
                this.externalMenu.content.link = serviceData.external_menu.link ?? "";
            }
            this.externalMenu.type = serviceData.external_menu_type;

            if (serviceData.special) {
                this.specialEnabled = true;
                this.applySpecialMode(true);
                if (serviceData.special_date_begin) {
                    this.service.general.special.openDate = this.getDateTime(serviceData.special_date_begin);
                }
                if (serviceData.special_date_end) {
                    this.service.general.special.closeDate = this.getDateTime(serviceData.special_date_end);
                }
            }
        },
        updateMenus(currentMenus) {
            this.service.menus.mandatory = currentMenus.config.isMandatory;
            this.service.menus.mandatoryAbovePax = currentMenus.config.nbPersMandatoryAbovePax;
            this.service.menus.mustBeUnique = currentMenus.config.isUnique;
            this.service.menus.isUniqueAbovePax = currentMenus.config.nbPersUniqueAbovePax;
            this.service.menus.value = currentMenus.checkedMenus;
        },
        updateGeneralOptions(checkedGeneralOptions) {
            this.service.generalOptions.value = checkedGeneralOptions;
        },
        updateExternalMenus(externalMenu) {
            this.externalMenu = externalMenu;
        },
        filterRestaurantsToSuggest(suggestedRestaurants) {
            return suggestedRestaurants.filter((suggestedRestaurant) => suggestedRestaurant.id !== Number.parseInt(this.restaurant_id));
        },
        getServicesOverrideByOpening() {
            return axios.post(`/api/restaurants/${this.restaurant_id}/services/openings/getServicesOverride`, this.formatServiceForForm());
        },
        postForm(e, confirm = false) {
            if (e) {
                e.preventDefault();
            }

            if (confirm) {
                this.showConfirmDeleteSlotsModal = false;
            }

            this.errors = [];
            this.formLoading = true;

            if (this.isEditing) {
                if (!confirm) {
                    axios
                        .post(`/api/restaurants/${this.restaurant_id}/services/${this.service_id}/checkFutureReservations`, this.cancelData)
                        .then((response) => {
                            if (response.data?.data?.length > 0) {
                                this.formLoading = false;
                                this.futuresReservations = response.data.data;
                                this.showConfirmDeleteSlotsModal = true;
                                return;
                            }
                            this.sendForm();
                        })
                        .catch((error) => {
                            this.errors = error.response?.data?.errors || [this.getErrorMsgFromErrorResponse(error)];
                            this.formLoading = false;
                        });
                } else {
                    this.sendForm();
                }
                return;
            }

            if (this.specialEnabled && !this.showConfirmModal) {
                this.getServicesOverrideByOpening()
                    .then((response) => {
                        if (!response.data.services_override) {
                            return this.sendForm();
                        }
                        const servicesId = response.data.services_override.join(",");
                        axios
                            .get(
                                `/api/restaurants/${this.restaurant_id}/reservations` +
                                    `/filter?begin=${this.formatDateToEn(this.service.general.special.openDate)}&end=${this.formatDateToEn(
                                        this.service.general.special.closeDate
                                    )}` +
                                    `&services=${servicesId}&excluded_status=pending-canceled-refused-noshow`
                            )
                            .then((response) => {
                                if (response.data.length > 0) {
                                    this.showConfirmModalServicesId = servicesId;
                                    this.showConfirmModal = true;
                                    this.formLoading = false;
                                } else {
                                    return this.sendForm();
                                }
                            });
                    })
                    .catch((error) => {
                        this.errors = error.response?.data?.errors || [this.getErrorMsgFromErrorResponse(error)];
                        this.formLoading = false;
                    });
                return;
            }

            this.sendForm();
        },
        sendForm() {
            this.errors = [];
            this.showConfirmModal = false;

            const formData = this.formatServiceForForm(this.isEditing ? "put" : "post");
            const url = this.isEditing
                ? `/api/restaurants/${this.restaurant_id}/services/${this.service_id}`
                : `/api/restaurants/${this.restaurant_id}/services`;

            axios
                .post(url, formData)
                .then((response) => {
                    this.formLoading = false;
                    this.notifySuccess(response);
                    this.stayOnSavedService(response);
                })
                .catch((error) => {
                    this.errors = error.response?.data?.errors || [this.getErrorMsgFromErrorResponse(error)];
                    this.formLoading = false;
                });
        },
        cancelReservations(cancelData) {
            this.formLoading = true;
            this.errors = [];

            axios
                .post(`/api/restaurants/${this.restaurant_id}/services/${this.service_id}/cancelFutureReservations`, {
                    ...this.cancelData,
                    ...cancelData,
                })
                .then((response) => {
                    this.showConfirmDeleteSlotsModal = false;
                    this.notifySuccess(response);
                    this.sendForm();
                })
                .catch((error) => {
                    this.errors = error.response?.data?.errors || [this.getErrorMsgFromErrorResponse(error)];
                    this.formLoading = false;
                });
        },
        formatSecurePaymentForForm() {
            const cardCaptureEnabled = Boolean(this.service.payment.bankImprint.enabled);
            const noShowGuaranteeEnabled = cardCaptureEnabled && Boolean(this.service.payment.bankImprint.noShowGuaranteeEnabled);
            const bankImprintEnabled = cardCaptureEnabled && Boolean(this.service.payment.bankImprint.securityEnabled);
            const prepaymentMode = this.service.payment.prepayment.calculationMode || "cart_percentage";
            const prepaymentEnabled = cardCaptureEnabled && Boolean(this.service.payment.prepayment.enabled);
            const noShowAmount = noShowGuaranteeEnabled ? Number(this.service.payment.bankImprint.amount || 0) : 0;
            const cardCapturePax = cardCaptureEnabled ? Math.max(1, Number(this.service.payment.bankImprint.cardCaptureEnabledAbovePax || 1)) : null;
            const bankImprintPax = bankImprintEnabled
                ? Math.max(cardCapturePax || 1, Number(this.service.payment.bankImprint.enabledAbovePax || cardCapturePax || 1))
                : null;
            const prepaymentPax = prepaymentEnabled
                ? Math.max(cardCapturePax || 1, Number(this.service.payment.prepayment.enabledAbovePax || cardCapturePax || 1))
                : null;
            const enabledThresholds = [cardCapturePax, bankImprintPax, prepaymentPax].filter(
                (threshold) => threshold !== null && threshold !== undefined && threshold !== ""
            );

            return {
                card_capture_enabled: cardCaptureEnabled,
                card_capture_enabled_above_pax: enabledThresholds.length ? Math.min(...enabledThresholds.map(Number)) : null,
                no_show_amount_per_cover_cents: Math.round(noShowAmount * 100),
                no_show_amount_mode: this.service.payment.bankImprint.amountMode || "per_cover",
                currency: "EUR",
                bank_imprint_enabled: bankImprintEnabled,
                bank_imprint_enabled_above_pax: bankImprintPax,
                bank_imprint_failure_action: this.service.payment.autoCancel ? "cancel_reservation" : "manual_review",
                bank_imprint_capture_lead_days: Math.min(this.service.payment.bankImprint.captureLeadDays || 3, 3),
                prepayment_enabled: prepaymentEnabled,
                prepayment_allow_pay_on_site: Boolean(this.service.payment.prepayment.allowPayOnSite),
                prepayment_calculation_mode: prepaymentMode,
                prepayment_percent: prepaymentMode === "cart_percentage" ? this.service.payment.prepayment.percentage : null,
                prepayment_fixed_amount_cents:
                    prepaymentMode === "cart_percentage" ? null : Math.round(Number(this.service.payment.prepayment.fixedAmount || 0) * 100),
                prepayment_enabled_above_pax: prepaymentPax,
            };
        },
        formatServiceForForm(method = "post") {
            const formData = new FormData();
            const data = {
                name: this.service.general.name,
                category: this.service.general.category.value,
                custom_category_fr: this.service.general.category.nameFr,
                custom_category_en: this.service.general.category.nameEn,
                days: this.service.general.openDays,
                hour_begin: this.service.general.openHours.start,
                hour_end: this.service.general.openHours.end,
                default_slot_max_pax: this.service.general.pax.max,
                default_slot_max_reservations: this.service.general.defaultSlotMaxReservations,
                max_pax: this.service.general.pax.max,
                lock_reservation_until_enabled: this.service.general.lock.enabled,
                lock_reservation_until: this.service.general.lock.until,
                lock_reservation_until_unit: this.service.general.lock.unit,
                special: this.specialEnabled,
                slots: this.service.general.slots,
                menu_mandatory: this.service.menus.mandatory,
                menu_mandatory_from: this.service.menus.mandatoryAbovePax,
                menu_unique: this.service.menus.mustBeUnique,
                menu_unique_from: this.service.menus.isUniqueAbovePax,
                prepayment_enabled: this.service.payment.prepayment.enabled,
                prepayment_percent: this.service.payment.prepayment.percentage,
                prepayment_mandatory: !this.service.payment.prepayment.allowPayOnSite,
                prepayment_min_pers: this.service.payment.prepayment.enabledAbovePax,
                menus: this.service.menus.value,
                general_options_selected: this.service.generalOptions.value,
                alternative_restaurants: this.service.full.restaurantsProposals,
                autoconfirm: this.service.general.autoconfirm.enabled,
                autoconfirm_percent: this.service.general.autoconfirm.upToPercentFull,
                autoconfirm_max_pers: this.service.general.autoconfirm.upToPax,
                autoconfirm_until: this.service.general.autoconfirm.upToMinutesBeforeStart,
                lock_nb_noshow: this.service.general.autoconfirm.upToNoshow,
                cancellation_until: this.service.general.cancellation.delay,
                enable_footprint: this.service.payment.bankImprint.enabled && this.service.payment.bankImprint.securityEnabled,
                footprint_min_pers: this.service.payment.bankImprint.enabledAbovePax,
                amount_noshow: this.service.payment.bankImprint.amount,
                option_bank_auto_cancel: this.service.payment.autoCancel,
                secure_payment: this.formatSecurePaymentForForm(),
                is_table_rotation_enabled: this.service.paxes.rotation,
                paxes_durations: this.service.paxes.value,
                enable_pax_per_slot: this.service.general.pax.perSlot,
                is_seating_plan_algorithm_enabled: this.service.seatingPlan.value ? this.service.seatingPlan.algorithm_enabled : false,
                seating_plan_id: this.service.seatingPlan.value ? this.service.seatingPlan.value.id : null,
                use_widget_min_pers: this.service.general.use_widget_min_pers,
                use_widget_max_pers: this.service.general.use_widget_max_pers,
                min_pers: this.service.general.min_pers,
                max_pers: this.service.general.max_pers,
                allow_clients_choose_room:
                    this.service.seatingPlan.value && this.service.seatingPlan.algorithm_enabled
                        ? this.service.seatingPlan.allow_clients_choose_room
                        : false,
                allow_clients_choose_room_mandatory:
                    this.service.seatingPlan.value && this.service.seatingPlan.algorithm_enabled && this.service.seatingPlan.allow_clients_choose_room
                        ? this.service.seatingPlan.allow_clients_choose_room_mandatory
                        : 0,
                show_on_widget: this.service.general.show_on_widget,
                reminder_payment_is_auto: this.service.sendReminderPayment.isAuto,
                external_menu: this.externalMenu.content,
                external_menu_type: this.externalMenu.type,
                enable_google_reserve: this.service.googleReserveSettings.enabled,
            };

            if (this.specialEnabled) {
                data.special_date_begin = this.service.general.special.openDate.toISODate();
                data.special_date_end = this.service.general.special.closeDate.toISODate();
                data.cancel_enabled = this.confirmModalData.cancel_enabled;
                data.message_object = this.confirmModalData.message_object;
                data.message_body = this.confirmModalData.message_body;
                data.reservations_ids = this.confirmModalData.reservations_ids;
            }

            if (this.service.sendReminderPayment.isAuto) {
                data.reminder_payment_hours = this.service.sendReminderPayment.hours;
            }

            if (this.isDuplicating) {
                data.is_duplicating = this.isDuplicating;
            }

            if (method === "put") {
                formData.append("_method", "PUT");
            }

            if (this.isValidFile(this.externalMenu.file) && this.externalMenu.type === this.EXTERNAL_MENU_FILE.value) {
                formData.append("file", this.externalMenu.file);
            }

            formData.append("payload", JSON.stringify(data));
            return formData;
        },
        formatDateToEn(date) {
            return this.getDateTime(date).toISODate();
        },
        goBack() {
            const routeName = this.specialEnabled ? "booking.restaurants.settings.openings" : "booking.restaurants.settings.services";
            this.$router.push({
                name: routeName,
                params: {
                    restaurant_id: this.restaurant_id,
                },
            });
        },
        stayOnSavedService(response) {
            if (this.isEditing) {
                return;
            }

            const serviceId = response?.data?.service_id;
            if (!serviceId) {
                return;
            }

            this.$router.replace({
                name: "booking.restaurants.settings.services.builder.edit",
                params: {
                    restaurant_id: this.restaurant_id,
                    service_id: serviceId,
                },
            });
        },
    },
    components: {
        GeneralGroup,
        MenuAndOptionsGroup,
        PaymentGroup,
        MiscGroup,
        ServiceBuilderHeader,
        googleReserve,
        LoaderComponent,
        NoSeatingPlanModal,
        ShowErrors,
        ConfirmOpeningCreateModal,
        ConfirmDeleteSlotsModal,
    },
};
</script>
